// src/ui/screens/post/EditListingPage.jsx

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { db, storage } from '../../../firebase';
import {
  doc,
  updateDoc,
  deleteField,
  serverTimestamp,
} from 'firebase/firestore';
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from 'firebase/storage';
import {
  propertyCategories,
  getCategoryByTitle,
} from '../../../utils/propertyCategories';
import { sortedDistricts, getDistrictByName } from '../../../utils/districts';
import { getDivisionsForDistrict } from '../../../utils/divisions';
import { getSpecificFields } from '../../../utils/propertySpecificFields';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
  cardLight: '#2C2F36',
};

const MAX_VIDEO_SECONDS = 30;
const MAX_VIDEO_MB = 80;
const MAX_IMAGES = 10;

// ── Helper: format duration ────────────────────────────────────────────────

const formatDuration = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

// ── EditListingPage ──────────────────────────────────────────────────────────

export const EditListingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const listingId = location.state?.listingId || '';
  const listing = location.state?.listing || {};

  // ── Form state ────────────────────────────────────────────────────────────

  const [title, setTitle] = useState(listing.title || '');
  const [description, setDescription] = useState(listing.description || '');
  const [price, setPrice] = useState(
    listing.isNegotiable ? '' : (listing.price ? String(listing.price) : '')
  );
  const [address, setAddress] = useState(listing.address || '');
  const [phone, setPhone] = useState(
    (listing.contactPhone || '').startsWith('+256') 
      ? (listing.contactPhone || '').substring(4) 
      : (listing.contactPhone || '')
  );
  const [isNegotiable, setIsNegotiable] = useState(listing.isNegotiable || false);

  const [selectedCategory, setSelectedCategory] = useState(
    getCategoryByTitle(listing.category)
  );
  const [selectedSubcategory, setSelectedSubcategory] = useState(listing.subcategory || null);
  const [selectedDistrict, setSelectedDistrict] = useState(
    getDistrictByName(listing.district)
  );
  const [selectedDivision, setSelectedDivision] = useState(null);

  const [specificValues, setSpecificValues] = useState(listing.specificFields || {});
  const [fieldErrors, setFieldErrors] = useState({});

  // ── Media state ───────────────────────────────────────────────────────────

  const [existingImageUrls, setExistingImageUrls] = useState(listing.imageUrls || []);
  const [newImages, setNewImages] = useState([]);
  const [existingVideoUrl, setExistingVideoUrl] = useState(listing.videoUrl || null);
  const [existingThumbnailUrl, setExistingThumbnailUrl] = useState(listing.thumbnailUrl || null);
  const [existingVideoRemoved, setExistingVideoRemoved] = useState(false);

  const [videoFile, setVideoFile] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [thumbnailFile, setThumbnailFile] = useState(null);

  // ── Upload state ─────────────────────────────────────────────────────────

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatusText, setUploadStatusText] = useState('');

  // ── Refs ──────────────────────────────────────────────────────────────────

  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);

  // ── Initialize division ──────────────────────────────────────────────────

  useEffect(() => {
    if (selectedDistrict) {
      const divisions = getDivisionsForDistrict(selectedDistrict.name);
      const div = divisions.find(d => d.name === listing.division);
      if (div) setSelectedDivision(div);
    }
  }, [selectedDistrict, listing.division]);

  // ── Computed ──────────────────────────────────────────────────────────────

  const totalImages = existingImageUrls.length + newImages.length;
  const videoChanged = videoFile !== null || existingVideoRemoved;

  // ── Validation ────────────────────────────────────────────────────────────

  const validate = () => {
    const errors = {};

    if (!selectedCategory) errors.category = 'Please select a category';
    if (!selectedSubcategory) errors.subcategory = 'Please select a sub-category';
    if (!title.trim()) errors.title = 'Title is required';
    if (!description.trim()) errors.description = 'Description is required';
    if (!isNegotiable && !price.trim()) {
      errors.price = 'Enter a price or mark as negotiable';
    }
    if (!selectedDistrict) errors.district = 'Please select a district';
    if (!selectedDivision) errors.division = 'Please select a division';
    if (!address.trim()) errors.address = 'Address is required';
    if (!phone.trim()) errors.phone = 'Contact phone is required';
    if (totalImages === 0 && !existingVideoUrl && !videoFile) {
      errors.media = 'Add at least one photo or a video';
    }

    const fields = getSpecificFields(
      selectedCategory?.title || '',
      selectedSubcategory || ''
    );
    for (const field of fields) {
      if (field.required) {
        const val = specificValues[field.name] || '';
        if (!val.trim()) {
          errors[`sf_${field.name}`] = `${field.label} is required`;
        }
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handlePickCategory = () => {
    navigate('/category-picker');
  };

  const handlePickDistrict = () => {
    navigate('/district-picker');
  };

  const handlePickDivision = () => {
    if (!selectedDistrict) {
      alert('Please select a district first');
      return;
    }
    navigate('/division-picker', {
      state: { districtName: selectedDistrict.name },
    });
  };

  const handlePickImages = () => {
    if (totalImages >= MAX_IMAGES) {
      alert(`Maximum ${MAX_IMAGES} images allowed`);
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const remaining = MAX_IMAGES - totalImages;
    const toAdd = files.slice(0, remaining);
    const newImageItems = toAdd.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));

    setNewImages([...newImages, ...newImageItems]);
    e.target.value = '';
  };

  const handleRemoveExistingImage = (index) => {
    setExistingImageUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveNewImage = (index) => {
    const newImagesCopy = [...newImages];
    URL.revokeObjectURL(newImagesCopy[index].preview);
    newImagesCopy.splice(index, 1);
    setNewImages(newImagesCopy);
  };

  const handlePickVideo = () => {
    videoInputRef.current?.click();
  };

  const handleVideoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Size check
    if (file.size > MAX_VIDEO_MB * 1024 * 1024) {
      alert(`Video is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max is ${MAX_VIDEO_MB} MB.`);
      e.target.value = '';
      return;
    }

    // Duration check
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      const seconds = Math.floor(video.duration);
      if (seconds > MAX_VIDEO_SECONDS) {
        alert(`Video is ${seconds}s long. Max is ${MAX_VIDEO_SECONDS} seconds.`);
        e.target.value = '';
        return;
      }
      setVideoDuration(seconds);
      setVideoFile(file);
      setVideoPreviewUrl(URL.createObjectURL(file));
      setExistingVideoRemoved(true);
      
      // Generate thumbnail
      video.currentTime = 1;
      video.onseeked = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 720;
        canvas.height = 720 * (video.videoHeight / video.videoWidth);
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          if (blob) {
            setThumbnailFile(new File([blob], 'thumbnail.jpg', { type: 'image/jpeg' }));
          }
        }, 'image/jpeg', 0.75);
      };
    };
    video.src = URL.createObjectURL(file);
  };

  const handleRemoveExistingVideo = () => {
    setExistingVideoUrl(null);
    setExistingThumbnailUrl(null);
    setExistingVideoRemoved(true);
  };

  const handleRemoveNewVideo = () => {
    if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    setVideoFile(null);
    setVideoPreviewUrl(null);
    setVideoDuration(0);
    setThumbnailFile(null);
    setExistingVideoRemoved(
      listing.videoUrl ? false : true
    );
    setExistingVideoUrl(listing.videoUrl || null);
    setExistingThumbnailUrl(listing.thumbnailUrl || null);
    if (videoInputRef.current) videoInputRef.current.value = '';
  };

  const handleSpecificFieldChange = (name, value) => {
    setSpecificValues({ ...specificValues, [name]: value });
    setFieldErrors({ ...fieldErrors, [`sf_${name}`]: undefined });
  };

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!validate()) {
      alert('Please fill all required fields');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadStatusText('Preparing update…');

    try {
      const uid = user.uid;
      const imageUrls = [...existingImageUrls];
      let videoUrl = existingVideoRemoved ? null : existingVideoUrl;
      let thumbnailUrl = existingVideoRemoved ? null : existingThumbnailUrl;

      const totalMediaSteps = newImages.length + (videoFile ? 2 : 0);
      let completedSteps = 0;

      // ── Upload new images ──────────────────────────────────────────────
      for (let i = 0; i < newImages.length; i++) {
        setUploadStatusText(`Uploading photo ${i + 1} of ${newImages.length}…`);
        const suffix = Date.now() + i;
        const imageRef = ref(storage, `listings/${uid}/${listingId}/image_${suffix}.jpg`);
        const task = uploadBytesResumable(imageRef, newImages[i].file);

        task.on('state_changed', (snapshot) => {
          const imageProgress = snapshot.bytesTransferred / snapshot.totalBytes;
          const overall = 0.05 + (completedSteps + imageProgress) / totalMediaSteps * 0.75;
          setUploadProgress(overall);
        });

        await task;
        const url = await getDownloadURL(imageRef);
        imageUrls.push(url);
        completedSteps++;
      }

      // ── Upload new video ──────────────────────────────────────────────
      if (videoFile) {
        setUploadStatusText('Uploading video…');
        const ext = videoFile.name.split('.').pop() || 'mp4';
        const videoRef = ref(storage, `listings/${uid}/${listingId}/atom_${Date.now()}.${ext}`);
        const task = uploadBytesResumable(videoRef, videoFile);

        task.on('state_changed', (snapshot) => {
          const videoProgress = snapshot.bytesTransferred / snapshot.totalBytes;
          const overall = 0.05 + (completedSteps + videoProgress) / totalMediaSteps * 0.75;
          setUploadProgress(overall);
        });

        await task;
        videoUrl = await getDownloadURL(videoRef);
        completedSteps++;

        // ── Upload thumbnail ────────────────────────────────────────────
        if (thumbnailFile) {
          setUploadStatusText('Uploading thumbnail…');
          const thumbRef = ref(storage, `listings/${uid}/${listingId}/thumbnail_${Date.now()}.jpg`);
          const thumbTask = uploadBytesResumable(thumbRef, thumbnailFile);

          thumbTask.on('state_changed', (snapshot) => {
            const thumbProgress = snapshot.bytesTransferred / snapshot.totalBytes;
            const overall = 0.05 + (completedSteps + thumbProgress) / totalMediaSteps * 0.75;
            setUploadProgress(overall);
          });

          await thumbTask;
          thumbnailUrl = await getDownloadURL(thumbRef);
          completedSteps++;
        }
      }

      setUploadProgress(0.85);
      setUploadStatusText('Saving changes…');

      // ── Update Firestore ──────────────────────────────────────────────
      const updateData = {
        category: selectedCategory.title,
        subcategory: selectedSubcategory,
        title: title.trim(),
        description: description.trim(),
        currency: 'UGX',
        price: isNegotiable ? null : parseFloat(price) || null,
        isNegotiable: isNegotiable,
        country: 'Uganda',
        district: selectedDistrict.name,
        division: selectedDivision.name,
        address: address.trim(),
        contactPhone: `+256${phone.trim()}`,
        imageUrls: imageUrls,
        hasVideo: !!videoUrl,
        specificFields: specificValues,
        updatedAt: serverTimestamp(),
      };

      if (videoUrl !== null) {
        updateData.videoUrl = videoUrl;
        if (thumbnailUrl !== null) updateData.thumbnailUrl = thumbnailUrl;
      } else {
        updateData.videoUrl = deleteField();
        updateData.thumbnailUrl = deleteField();
      }

      await updateDoc(doc(db, 'listings', listingId), updateData);

      setUploadProgress(1.0);
      setUploadStatusText('Updated successfully!');

      setTimeout(() => {
        navigate(-1);
      }, 800);
    } catch (error) {
      console.error('Update error:', error);
      setIsUploading(false);
      alert('Update failed. Please try again.');
    }
  };

  // ── Discard confirmation ─────────────────────────────────────────────────

  const handleDiscard = () => {
    if (window.confirm('Discard changes? Your unsaved edits will be lost.')) {
      navigate(-1);
    }
  };

  // ── Render helpers ──────────────────────────────────────────────────────

  const renderSection = (icon, title, children, subtitle = null) => (
    <div className="mb-4 p-4 rounded-xl" style={{ backgroundColor: COLORS.card }}>
      <div className="flex items-center mb-3.5">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${COLORS.orange}1F` }}
        >
          <svg className="w-4.5 h-4.5" fill="none" stroke={COLORS.orange} viewBox="0 0 24 24" strokeWidth={2}>
            {icon === 'category' && (
              <>
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
              </>
            )}
            {icon === 'edit' && (
              <>
                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
              </>
            )}
            {icon === 'price' && (
              <>
                <path d="M12 2v20" />
                <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
              </>
            )}
            {icon === 'location' && (
              <>
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                <circle cx="12" cy="10" r="3" />
              </>
            )}
            {icon === 'phone' && (
              <>
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
              </>
            )}
            {icon === 'media' && (
              <>
                <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </>
            )}
            {icon === 'tune' && (
              <>
                <path d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </>
            )}
          </svg>
        </div>
        <span className="text-white font-bold text-sm ml-2.5">{title}</span>
        {subtitle && (
          <>
            <span className="flex-1" />
            <span className="text-white/40 text-xs">{subtitle}</span>
          </>
        )}
      </div>
      <div className="h-px bg-white/5 mb-3.5" />
      {children}
    </div>
  );

  const renderPickerField = (label, value, subtitle, error, onTap, icon = null) => {
    const hasValue = value && value.length > 0;
    return (
      <div className="mb-3 last:mb-0">
        <button
          onClick={onTap}
          className="w-full p-3.5 rounded-xl text-left flex items-center gap-2"
          style={{
            backgroundColor: COLORS.cardLight,
            border: error ? '1px solid #FF1744' : 'none',
          }}
        >
          {icon && <span className="flex-shrink-0">{icon}</span>}
          <div className="flex-1 min-w-0">
            <span className={hasValue ? 'text-white text-sm font-medium' : 'text-white/40 text-sm'}>
              {hasValue ? value : label}
            </span>
            {subtitle && (
              <div className="text-white/40 text-xs mt-0.5">{subtitle}</div>
            )}
          </div>
          <svg className="w-5 h-5 text-filatom-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        {error && <div className="text-red-500 text-xs mt-1 ml-1">{error}</div>}
      </div>
    );
  };

  const renderTextField = (label, value, onChange, placeholder, error, options = {}) => {
    const {
      type = 'text',
      maxLines = 1,
      prefix = null,
      inputMode = null,
    } = options;

    return (
      <div className="mb-3 last:mb-0">
        <div
          className="rounded-xl overflow-hidden"
          style={{
            backgroundColor: COLORS.cardLight,
            border: error ? '1px solid #FF1744' : 'none',
          }}
        >
          <div className="flex items-center">
            {prefix && (
              <span className="text-white/70 text-sm pl-3.5 flex-shrink-0">{prefix}</span>
            )}
            <input
              type={type}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              className="w-full bg-transparent text-white text-sm outline-none px-3.5 py-3.5 placeholder:text-white/20"
              style={{ minHeight: maxLines > 1 ? 80 : 44 }}
              inputMode={inputMode}
            />
          </div>
        </div>
        {error && <div className="text-red-500 text-xs mt-1 ml-1">{error}</div>}
      </div>
    );
  };

  const renderSubcategoryField = () => {
    if (!selectedCategory) return null;
    const subs = selectedCategory.subCategories || [];

    return (
      <div className="mt-3">
        <div className="text-white/50 text-xs font-medium mb-2">Sub-category</div>
        <div className="flex flex-wrap gap-2">
          {subs.map((sub) => {
            const isSelected = selectedSubcategory === sub;
            return (
              <button
                key={sub}
                onClick={() => {
                  setSelectedSubcategory(sub);
                  setSpecificValues({});
                  setFieldErrors({ ...fieldErrors, subcategory: undefined });
                }}
                className={`px-3.5 py-2 rounded-full text-xs font-medium transition-all ${
                  isSelected
                    ? 'bg-filatom-primary text-black'
                    : 'bg-filatom-cardLight text-white/70'
                }`}
              >
                {sub}
              </button>
            );
          })}
        </div>
        {fieldErrors.subcategory && (
          <div className="text-red-500 text-xs mt-1 ml-1">{fieldErrors.subcategory}</div>
        )}
      </div>
    );
  };

  const renderVideoArea = () => {
    // Case 1: New video picked
    if (videoFile) {
      return (
        <div className="rounded-xl overflow-hidden border border-filatom-primary/40">
          <div className="relative bg-filatom-cardLight">
            {videoPreviewUrl && (
              <video
                src={videoPreviewUrl}
                className="w-full aspect-video object-cover"
                controls
              />
            )}
            <div className="absolute top-2 left-2 px-2 py-0.5 rounded bg-filatom-primary">
              <span className="text-black text-[10px] font-bold">New · Atom</span>
            </div>
            {videoDuration > 0 && (
              <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-black/70">
                <span className="text-white text-[11px] font-semibold">{formatDuration(videoDuration)}</span>
              </div>
            )}
          </div>
          <div className="p-2.5 flex items-center gap-2" style={{ backgroundColor: COLORS.cardLight }}>
            <svg className="w-4.5 h-4.5 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <span className="text-white/70 text-xs flex-1 truncate">{videoFile.name}</span>
            <button
              onClick={handleRemoveNewVideo}
              className="p-1.5 rounded-lg bg-red-500/15"
            >
              <svg className="w-4.5 h-4.5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      );
    }

    // Case 2: Existing video present (not removed)
    if (existingVideoUrl) {
      return (
        <div className="rounded-xl overflow-hidden border border-filatom-primary/40">
          <div className="relative bg-filatom-cardLight aspect-video">
            {existingThumbnailUrl ? (
              <img
                src={existingThumbnailUrl}
                alt="Video thumbnail"
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-filatom-cardLight">
                <svg className="w-10 h-10 text-white/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
            )}
            <div className="absolute top-2 left-2 px-2 py-0.5 rounded bg-green-500/85">
              <span className="text-white text-[10px] font-bold">Saved · Atom</span>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center">
                <svg className="w-7.5 h-7.5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="p-2.5 flex items-center gap-2" style={{ backgroundColor: COLORS.cardLight }}>
            <svg className="w-4.5 h-4.5 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <span className="text-white/70 text-xs flex-1">Existing video</span>
            <button
              onClick={handlePickVideo}
              className="px-2.5 py-1.5 rounded-lg text-xs font-semibold"
              style={{ backgroundColor: `${COLORS.orange}26`, color: COLORS.orange }}
            >
              Replace
            </button>
            <button
              onClick={handleRemoveExistingVideo}
              className="p-1.5 rounded-lg bg-red-500/15"
            >
              <svg className="w-4.5 h-4.5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      );
    }

    // Case 3: No video
    return (
      <button
        onClick={handlePickVideo}
        className="w-full h-[90px] rounded-xl flex flex-col items-center justify-center gap-1.5 border border-filatom-primary/35"
        style={{ backgroundColor: COLORS.cardLight }}
      >
        <svg className="w-7.5 h-7.5 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="text-filatom-primary text-xs font-medium">Add Video</span>
      </button>
    );
  };

  const renderImageGrid = () => {
    const existingCount = existingImageUrls.length;
    const newCount = newImages.length;
    const showAddButton = totalImages < MAX_IMAGES;
    const itemCount = existingCount + newCount + (showAddButton ? 1 : 0);

    return (
      <div className="grid grid-cols-3 gap-2">
        {existingImageUrls.map((url, index) => (
          <div key={`existing-${index}`} className="relative aspect-square rounded-xl overflow-hidden">
            <img src={url} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" />
            {index === 0 && (
              <div className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded bg-green-500/85">
                <span className="text-white text-[10px] font-bold">Cover</span>
              </div>
            )}
            <button
              onClick={() => handleRemoveExistingImage(index)}
              className="absolute top-1 right-1 w-5.5 h-5.5 rounded-full bg-black/80 flex items-center justify-center"
            >
              <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}
        {newImages.map((img, index) => (
          <div key={`new-${index}`} className="relative aspect-square rounded-xl overflow-hidden">
            <img src={img.preview} alt={`New photo ${index + 1}`} className="w-full h-full object-cover" />
            <div className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded bg-filatom-primary">
              <span className="text-black text-[10px] font-bold">New</span>
            </div>
            <button
              onClick={() => handleRemoveNewImage(index)}
              className="absolute top-1 right-1 w-5.5 h-5.5 rounded-full bg-black/80 flex items-center justify-center"
            >
              <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}
        {showAddButton && (
          <button
            onClick={handlePickImages}
            className="aspect-square rounded-xl flex flex-col items-center justify-center border border-filatom-primary/40"
            style={{ backgroundColor: COLORS.cardLight }}
          >
            <svg className="w-7 h-7 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span className="text-filatom-primary text-[11px] font-medium mt-1">
              {totalImages === 0 ? 'Add Photos' : 'Add More'}
            </span>
          </button>
        )}
      </div>
    );
  };

  const renderSpecificFields = () => {
    if (!selectedCategory || !selectedSubcategory) return null;
    const fields = getSpecificFields(selectedCategory.title, selectedSubcategory);
    if (fields.length === 0) return null;

    return renderSection(
      'tune',
      'Property Details',
      fields.map((field) => {
        const name = field.name;
        const label = field.label;
        const type = field.type;
        const required = field.required || false;
        const errorKey = `sf_${name}`;

        if (type === 'select') {
          const options = field.options || [];
          return (
            <div key={name} className="mb-3 last:mb-0">
              <div className="text-white/50 text-xs font-medium mb-1.5">{required ? `${label} *` : label}</div>
              <div className="flex flex-wrap gap-2">
                {options.map((opt) => {
                  const isSelected = specificValues[name] === opt;
                  return (
                    <button
                      key={opt}
                      onClick={() => handleSpecificFieldChange(name, opt)}
                      className={`px-3.5 py-2 rounded-full text-xs font-medium transition-all ${
                        isSelected
                          ? 'bg-filatom-primary text-black'
                          : 'bg-filatom-cardLight text-white/70'
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
              {fieldErrors[errorKey] && (
                <div className="text-red-500 text-xs mt-1 ml-1">{fieldErrors[errorKey]}</div>
              )}
            </div>
          );
        }

        return (
          <div key={name} className="mb-3 last:mb-0">
            <div
              className="rounded-xl overflow-hidden"
              style={{
                backgroundColor: COLORS.cardLight,
                border: fieldErrors[errorKey] ? '1px solid #FF1744' : 'none',
              }}
            >
              <input
                type={type === 'number' ? 'number' : 'text'}
                value={specificValues[name] || ''}
                onChange={(e) => handleSpecificFieldChange(name, e.target.value)}
                placeholder={`${required ? '*' : ''} ${label}`}
                className="w-full bg-transparent text-white text-sm outline-none px-3.5 py-3.5 placeholder:text-white/20"
                inputMode={type === 'number' ? 'numeric' : 'text'}
              />
            </div>
            {fieldErrors[errorKey] && (
              <div className="text-red-500 text-xs mt-1 ml-1">{fieldErrors[errorKey]}</div>
            )}
          </div>
        );
      })
    );
  };

  // ── Main render ───────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-filatom-bg">
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleFileChange}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        className="hidden"
        onChange={handleVideoChange}
      />

      {/* Top Bar */}
      <div className="flex items-center px-3 pt-2 pb-1 gap-2">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full flex items-center justify-center border border-white/10"
          style={{ backgroundColor: COLORS.card }}
        >
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <span className="text-white text-xl font-bold flex-1">Edit Listing</span>
        <button
          onClick={handleDiscard}
          className="px-3.5 py-2 rounded-full text-xs font-semibold"
          style={{
            backgroundColor: 'rgba(255,23,68,0.10)',
            color: '#FF1744',
            border: '1px solid rgba(255,23,68,0.30)',
          }}
        >
          Discard
        </button>
      </div>

      {/* Form Content */}
      <div className="px-4 pb-8">
        {/* Category Section */}
        {renderSection(
          'category',
          'Category',
          <>
            {renderPickerField(
              'Category',
              selectedCategory?.title,
              null,
              fieldErrors.category,
              handlePickCategory,
              selectedCategory && (
                <span className="text-filatom-primary text-sm">{selectedCategory.title}</span>
              )
            )}
            {renderSubcategoryField()}
          </>
        )}

        {/* Listing Details */}
        {renderSection(
          'edit',
          'Listing Details',
          <>
            {renderTextField('Listing Title', title, setTitle, 'e.g. 3-Bedroom House in Kololo', fieldErrors.title)}
            {renderTextField('Description', description, setDescription, 'Describe the property in detail…', fieldErrors.description, { maxLines: 5 })}
          </>
        )}

        {/* Pricing */}
        {renderSection(
          'price',
          'Pricing',
          <>
            <div className="p-3 rounded-xl flex items-center gap-2.5 border border-filatom-primary/30" style={{ backgroundColor: `${COLORS.orange}1A` }}>
              <svg className="w-4.5 h-4.5 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v1m0 7V14m0 1v1m0 0c1.11 0 2.08-.402 2.599-1M12 16v1m0-1v-1" />
              </svg>
              <span className="text-filatom-primary text-sm font-medium">Currency: Ugandan Shillings (UGX)</span>
            </div>

            {!isNegotiable && renderTextField('Price (UGX)', price, setPrice, 'e.g. 250000000', fieldErrors.price, { type: 'number', inputMode: 'numeric' })}

            <button onClick={() => setIsNegotiable(!isNegotiable)} className="flex items-center gap-2.5 mt-2">
              <div
                className={`w-5.5 h-5.5 rounded flex items-center justify-center border-2 transition-all ${
                  isNegotiable ? 'bg-filatom-primary border-filatom-primary' : 'border-white/40'
                }`}
              >
                {isNegotiable && (
                  <svg className="w-3.5 h-3.5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span className="text-white/70 text-sm">Price is Negotiable</span>
            </button>
          </>
        )}

        {/* Location */}
        {renderSection(
          'location',
          'Location',
          <>
            <div className="p-3.5 rounded-xl flex items-center gap-2.5" style={{ backgroundColor: COLORS.cardLight }}>
              <span className="text-2xl">🇺🇬</span>
              <span className="text-white text-sm font-medium flex-1">Uganda</span>
              <svg className="w-4 h-4 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>

            {renderPickerField(
              'District',
              selectedDistrict?.name,
              selectedDistrict ? `${selectedDistrict.region} Region` : null,
              fieldErrors.district,
              handlePickDistrict
            )}

            {renderPickerField(
              'Division / Sub-county',
              selectedDivision?.name,
              selectedDistrict && !selectedDivision ? `Tap to select division in ${selectedDistrict.name}` : null,
              fieldErrors.division,
              handlePickDivision
            )}

            {renderTextField('Specific Address', address, setAddress, 'e.g. Plot 12, Ggaba Road, Bukoto', fieldErrors.address, { maxLines: 2 })}
          </>
        )}

        {/* Contact */}
        {renderSection(
          'phone',
          'Contact',
          renderTextField('Phone Number', phone, setPhone, '7XXXXXXXX', fieldErrors.phone, { prefix: '+256 ', inputMode: 'numeric' })
        )}

        {/* Media */}
        {renderSection(
          'media',
          'Photos & Video',
          <>
            {fieldErrors.media && <div className="text-red-500 text-xs mb-2">{fieldErrors.media}</div>}
            <div className="mb-3">
              <div className="flex items-center gap-1.5 mb-2">
                <svg className="w-4 h-4 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span className="text-white/50 text-xs font-medium">Atom (Video)</span>
                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ backgroundColor: `${COLORS.orange}1F`, color: COLORS.orange }}>
                  Optional · max 30s · 80 MB
                </span>
              </div>
              {renderVideoArea()}
            </div>

            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <svg className="w-4 h-4 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-white/50 text-xs font-medium">Photos</span>
                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ backgroundColor: `${COLORS.orange}1F`, color: COLORS.orange }}>
                  Optional · max 10
                </span>
                <span className="flex-1" />
                <span className="text-white/40 text-xs">{totalImages}/10</span>
              </div>
              {renderImageGrid()}
            </div>
          </>,
          `${totalImages}/10 photos${videoFile || existingVideoUrl ? ' · 1 video' : ''}`
        )}

        {/* Specific Fields */}
        {renderSpecificFields()}

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={isUploading}
          className="w-full h-13 rounded-xl bg-filatom-primary text-black font-bold text-sm flex items-center justify-center gap-2.5 transition-all hover:opacity-90 disabled:opacity-40"
        >
          <svg className="w-5.5 h-5.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
          </svg>
          Save Changes
        </button>
      </div>

      {/* Upload Overlay */}
      {isUploading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85">
          <div className="mx-10 p-7 rounded-2xl max-w-sm w-full" style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.orange}33` }}>
            <div className="flex flex-col items-center">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ backgroundColor: uploadProgress >= 1.0 ? `${COLORS.orange}26` : `${COLORS.orange}1A` }}
              >
                {uploadProgress >= 1.0 ? (
                  <svg className="w-9 h-9 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-8 h-8 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                )}
              </div>
              <span className={`text-3xl font-extrabold mt-5 ${uploadProgress >= 1.0 ? 'text-filatom-primary' : 'text-white'}`}>
                {uploadProgress >= 1.0 ? 'Updated!' : `${Math.round(uploadProgress * 100)}%`}
              </span>
              <span className="text-white/50 text-xs mt-1.5 text-center">{uploadStatusText}</span>
              <div className="w-full mt-5 h-1.5 rounded overflow-hidden bg-white/10">
                <div className="h-full rounded bg-filatom-primary transition-all duration-300" style={{ width: `${uploadProgress * 100}%` }} />
              </div>
              <div className="flex items-center gap-4 mt-3">
                {['Compress', 'Media', 'Saving', 'Done'].map((label, i) => {
                  const thresholds = [0.05, 0.10, 0.85, 1.0];
                  const active = uploadProgress >= thresholds[i];
                  return (
                    <div key={label} className="flex items-center">
                      <div className="flex flex-col items-center">
                        <div
                          className={`w-2.5 h-2.5 rounded-full transition-all duration-250 ${
                            active ? 'bg-filatom-primary' : 'bg-white/10'
                          }`}
                        />
                        <span className={`text-[10px] mt-1 ${active ? 'text-filatom-primary' : 'text-white/15'}`}>
                          {label}
                        </span>
                      </div>
                      {i < 3 && <div className="w-5 h-px bg-white/10 mx-0.5" />}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditListingPage;